import { useState, useMemo } from 'react'
import { Player, MatchStats } from '../types'
import { usePlayerStore } from '../stores'
import { useAuthStore } from '../stores'
import { FileText, Download, Edit3, Save, CheckCircle, TrendingUp } from 'lucide-react'

const POTENTIAL_LEVELS = [
  'Local Prospect',
  'Regional Talent',
  'National Level',
  'International Potential',
  'Elite Academy',
]

const getPotentialLabel = (value: Player['potential'] | string | undefined) => {
  let rating = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(rating)) {
    rating = 1
  }
  const safeIndex = Math.min(Math.max(Math.round(rating) - 1, 0), POTENTIAL_LEVELS.length - 1)
  return POTENTIAL_LEVELS[safeIndex]
}

interface ReportGeneratorProps {
  player: Player
  onClose: () => void
}

function ReportGenerator({ player, onClose }: ReportGeneratorProps) {
  const { updateScoutReport } = usePlayerStore()
  const [isEditing, setIsEditing] = useState(false)
  const [report, setReport] = useState(player.scout_report || '')
  const [saved, setSaved] = useState(false)

  const statsSummary = useMemo(() => {
    const history = player.match_history || []
    const totalMatches = history.length
    if (totalMatches === 0) return null

    const totals: MatchStats = history.reduce((acc, match) => {
      Object.keys(match.stats).forEach((k) => {
        const key = k as keyof MatchStats
        acc[key] = (acc[key] || 0) + (match.stats[key] || 0)
      })
      return acc
    }, {} as MatchStats)

    // Profile-level aggregates
    const profileTotals = {
      goals: player.goals || 0,
      assists: player.assists || 0,
      apps: player.apps || 0,
      kp: player.kp || 0,
      dr: player.dr || 0,
    }

    const combined = {
      goals: profileTotals.goals + (totals.goals || 0),
      assists: profileTotals.assists + (totals.assists || 0),
      apps: profileTotals.apps + totalMatches,
      key_passes: profileTotals.kp + (totals.key_passes || 0),
      dribbles: profileTotals.dr + (totals.dribbles || 0),
    }

    return {
      totals,
      profileTotals,
      combined,
      averages: Object.keys(totals).reduce((acc, k) => {
        const key = k as keyof MatchStats
        acc[key] = parseFloat((totals[key] / (totalMatches || 1)).toFixed(2))
        return acc
      }, {} as any),
      totalMatches,
    }
  }, [player.match_history])

  const potentialLabel = getPotentialLabel(player.potential)

  const generateDraft = () => {
    const careerHistory = player.career_history || []
    const historyBlock = careerHistory.length
      ? careerHistory
          .map((entry, index) => {
            const fee = entry.transaction_fee?.trim() ? entry.transaction_fee : 'N/A'
            return `${index + 1}. ${entry.years || 'N/A'} - ${entry.club || 'N/A'} | ${entry.goals ?? 'N/A'} goals, ${entry.assists ?? 'N/A'} assists, fee: ${fee}`
          })
          .join('\n')
      : 'No verified previous club history has been recorded on the profile.'

    const overviewParagraph = `${player.name} is a ${player.age}-year-old ${player.main_position || 'N/A'} currently attached to ${player.club || 'N/A'}. ${player.name} is assessed as a ${potentialLabel.toLowerCase()} based on the current profile, with room for further development depending on consistency, tactical fit, and the competitive level of matches observed.`

    const physicalParagraph = `Physically, the player presents as ${player.height ? `${player.height}cm` : 'unrecorded height'} and ${player.weight ? `${player.weight}kg` : 'unrecorded weight'}. Preferred foot is ${player.foot || 'N/A'}. These details suggest the player may be suited to roles that match the physical demands of the main position, especially where balance, mobility, and repeat actions are important.`

    const technicalParagraph = `From a technical standpoint, the current rating profile shows technical ${player.technical || 0}/10, physical ${player.physical || 0}/10, and mental ${player.mental || 0}/10. The balance of these attributes indicates the player can be evaluated not only on raw output, but also on repeatability, decision-making, and ability to execute under pressure.`

    const historyParagraph = careerHistory.length
      ? `Historical data suggests the player has progressed through the following clubs and seasons:\n${historyBlock}`
      : `Historical data is currently limited, so the recommendation should be treated as a profile-first assessment rather than a long-term trend analysis.`

    const matchParagraph = statsSummary
      ? `Tracked match evidence covers ${statsSummary.totalMatches} games. Across the monitored sample, the player has produced ${statsSummary.combined.goals} goals and ${statsSummary.combined.assists} assists, with ${statsSummary.combined.key_passes} key passes and ${statsSummary.combined.dribbles} successful dribbles where available. This suggests a player who can contribute both in direct end product and in buildup sequences.`
      : `No live match history is available yet, so the report leans on profile metrics and scouting notes. Once match recordings are added, this section can describe form trends, consistency, and game-by-game influence more accurately.`

    const roleParagraph = `In the context of ${player.main_position || 'the primary role'}, the player appears best evaluated for ${player.main_position ? `${player.main_position}-specific` : 'position-specific'} responsibilities such as spacing, timing of movement, pressing response, and final-third execution. If used in a different role, the report should consider how well the player adapts to inverted duties, transition phases, and off-ball discipline.`

    const developmentParagraph = `Development priorities should focus on improving repeatable strengths and closing visible gaps. If the player is a creator, the key question is how often they can influence play against stronger opposition. If the player is defensive, the focus should be on concentration, duels, and recovery actions. If the player is a forward, movement patterns, shot selection, and box timing should receive the most attention.`

    const verdictParagraph = potentialLabel.includes('Elite') || potentialLabel.includes('International')
      ? `Recommendation: high-priority recruitment target. The player should be shortlisted for detailed live scouting, video review, and role-specific comparison against top internal benchmarks.`
      : `Recommendation: promising prospect worth continued monitoring. The player should remain on the follow-up list with emphasis on consistency, tactical fit, and physical development over the next observation cycle.`

    let draft = `OFFICIAL SCOUTING REPORT: ${player.name} (${player.main_position || 'N/A'})\n`
    draft += `============================================================\n\n`
    draft += `1. EXECUTIVE SUMMARY\n${overviewParagraph}\n\n`
    draft += `2. PHYSICAL PROFILE\n${physicalParagraph}\n\n`
    draft += `3. TECHNICAL AND MENTAL PROFILE\n${technicalParagraph}\n\n`
    draft += `4. CAREER HISTORY\n${historyParagraph}\n\n`
    draft += `5. MATCH PERFORMANCE\n${matchParagraph}\n\n`
    draft += `6. ROLE FIT AND TACTICAL NOTES\n${roleParagraph}\n\n`
    draft += `7. DEVELOPMENT PRIORITIES\n${developmentParagraph}\n\n`
    draft += `8. SCOUT NOTES\n${player.notes || 'Standard developmental progression observed. Additional live observation recommended to confirm ceiling and tactical fit.'}\n\n`
    draft += `9. FINAL VERDICT\n${verdictParagraph}\n\n`
    draft += `This report is intended to support recruitment, squad planning, and future match observation. It should be updated as fresh match data, physical testing, and club history become available.`

    return draft
  }

  const { user } = useAuthStore()

  const handleSave = async () => {
    try {
      const author = user?.name || user?.email || 'Unknown'
      const saved_at = new Date().toISOString()
      await updateScoutReport(player.id, report, author, saved_at)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      setIsEditing(false)
      // keep local state as raw report body
      setReport(report)
    } catch (error) {
      console.error('Failed to save scout report:', error)
      window.alert('Failed to save report. Check your connection and try again.')
    }
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="bg-[#161b22] rounded-3xl shadow-2xl overflow-hidden border border-gray-800 print:shadow-none print:border-0 print:rounded-none">
      {/* Header */}
      <div className="bg-[#0b0f1a] p-8 text-white flex justify-between items-center print:bg-white print:text-black print:p-4 print:border-b-2 print:border-gray-900 border-b border-gray-800">
        <div>
          <h2 className="text-3xl font-black italic tracking-tighter uppercase mb-1 text-white">Scout Report Generator</h2>
          <p className="text-green-500 text-xs font-black uppercase tracking-[0.2em]">{player.name} • {player.club}</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <button
            onClick={handlePrint}
            className="p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition flex items-center gap-2 font-bold text-sm text-white"
          >
            <Download size={18} />
            Export PDF
          </button>
          <button
            onClick={onClose}
            className="p-3 bg-red-900/20 hover:bg-red-900/40 rounded-xl transition text-red-400"
          >
            Close
          </button>
        </div>
      </div>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-8 print:block print:p-0">
        {/* Left: Stats Summary */}
        <div className="lg:col-span-1 space-y-6 print:hidden">
          <div className="bg-[#0d1117] border border-gray-800 rounded-2xl p-6">
            <h3 className="text-xs font-black text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <TrendingUp size={14} className="text-green-500" />
              Combined Statistics
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#161b22] p-3 rounded-xl border border-gray-800">
                  <p className="text-[10px] text-gray-500 font-bold uppercase">Total Goals</p>
                  <p className="text-xl font-black text-white">{statsSummary ? statsSummary.combined.goals : player.goals}</p>
                </div>
                <div className="bg-[#161b22] p-3 rounded-xl border border-gray-800">
                  <p className="text-[10px] text-gray-500 font-bold uppercase">Total Assists</p>
                  <p className="text-xl font-black text-white">{statsSummary ? statsSummary.combined.assists : player.assists}</p>
                </div>
              </div>
              
              {statsSummary && (
                <div className="pt-4 border-t border-gray-800">
                  <p className="text-[10px] text-gray-500 font-black uppercase mb-2">Tracked Performance</p>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-400">Match Avg Goals</span>
                    <span className="text-xs font-bold text-white">{statsSummary.averages.goals}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-400">Match Avg Assists</span>
                    <span className="text-xs font-bold text-white">{statsSummary.averages.assists}</span>
                  </div>
                  <div className="p-3 bg-indigo-900/20 border border-indigo-800/30 rounded-xl mt-4">
                    <p className="text-[10px] text-indigo-400 font-black uppercase mb-1">Scout Insight</p>
                    <p className="text-[10px] text-gray-400">Recent match form accounts for {Math.round((statsSummary.totals.goals / (statsSummary.combined.goals || 1)) * 100)}% of total goal output.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Center/Right: Report Editor */}
        <div className="lg:col-span-2 space-y-4 print:w-full">
          <div className="flex justify-between items-center print:hidden">
            <h3 className="text-xs font-black text-gray-500 uppercase tracking-widest">Active Report</h3>
            <div className="flex gap-2">
              {!report && (
                <button
                  onClick={() => setReport(generateDraft())}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-black uppercase hover:bg-indigo-700"
                >
                  Auto-Draft Report
                </button>
              )}
              {isEditing ? (
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-xs font-black uppercase hover:bg-green-700"
                >
                  <Save size={14} />
                  Save Changes
                </button>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 text-white rounded-lg text-xs font-black uppercase hover:bg-gray-700"
                >
                  <Edit3 size={14} />
                  Edit Manually
                </button>
              )}
            </div>
          </div>

          <div className="relative group">
            {isEditing ? (
              <textarea
                value={report}
                onChange={(e) => setReport(e.target.value)}
                className="w-full h-[500px] p-8 border-2 border-green-500/20 bg-[#0d1117] rounded-3xl font-mono text-sm leading-relaxed text-white focus:ring-4 focus:ring-green-500/10 outline-none transition print:p-0 print:border-0 print:h-auto print:bg-white"
                placeholder="The formal scouting report will appear here..."
              />
            ) : (
              <div className="w-full min-h-[500px] p-8 bg-[#0d1117] rounded-3xl border border-gray-800 whitespace-pre-wrap font-mono text-sm leading-relaxed text-gray-300 print:bg-white print:border-0 print:p-0 print:text-base">
                {report || (
                  <div className="flex flex-col items-center justify-center h-[400px] text-gray-700">
                    <FileText size={64} className="mb-4 opacity-50" />
                    <p className="italic">No report has been drafted yet.</p>
                  </div>
                )}
              </div>
            )}
            
            {saved && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase flex items-center gap-1 animate-in slide-in-from-top-2">
                <CheckCircle size={10} />
                Persisted to Cloud
              </div>
            )}
          </div>

          <div className="hidden print:flex mt-10 border-t pt-4 text-xs text-gray-400 print:justify-between">
            <p>Scouta Integrated Talent Report • Confidential</p>
            <p>Generated on {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReportGenerator
